// "Open and closed" on the engagement overview: the decisions and milestones already recorded (from the audit log), in plain
// words, next to what is still open. Only acts that settle something are listed; the full log stays on the Activity page.
import type { Activity, OpenItem } from "../data/types";
import { fmtStamp } from "./format";

const ATTEST: Record<string, string> = {
  independence: "Independence", cpe: "CPE", license_peer_review: "Firm licence and peer review", acceptance: "Engagement acceptance",
  engagement_letter: "Signed engagement letter", predecessor: "Predecessor communication", research: "Public-source research", nonattest: "Nonattest services",
};
const PRED: Record<string, string> = {
  none: "no predecessor auditor", authorization_requested: "authorization to speak with them requested", authorized: "client authorized contact",
  authorization_refused: "client refused authorization", inquiries_sent: "inquiries sent", response_received: "response received", no_response: "no response",
};

const s = (v: unknown) => (typeof v === "string" ? v : typeof v === "number" ? String(v) : "");
const money = (v: unknown) => { const t = s(v); return t && !t.startsWith("$") ? `$${t}` : t; };

/** A plain sentence for an act that settled something, or null when the act is routine (drafts, reads, uploads …). */
export function decisionText(a: Activity): string | null {
  const i = a.inputs ?? {}, r = a.result ?? {};
  switch (a.command) {
    case "engagement.create": return "Engagement created";
    case "materiality.set": return `Materiality set: planning ${money(r.planning)}, performance ${money(r.performance)}, clearly trivial ${money(r.trivial)}`
      + (r.benchmark_amount ? ` (benchmark ${money(r.benchmark_amount)})` : "");
    case "attest.record": return `${ATTEST[s(i.subject)] ?? "Attestation"} attested${s(i.statement) ? `: “${s(i.statement)}”` : ""}`;
    case "independence.confirm": return `Independence confirmed${s(i.scope) === "annual" ? " (annual)" : " for this engagement"}`;
    case "predecessor.record": return `Predecessor${s(i.predecessor_firm) ? ` ${s(i.predecessor_firm)}` : ""}: ${PRED[s(i.status)] ?? s(i.status)}`;
    case "mapping.confirm": return Number(r.confirmed) > 0 ? `Account mapping confirmed (${s(r.confirmed)} account${Number(r.confirmed) === 1 ? "" : "s"})` : null;
    case "acceptance.decide": return "Acceptance decided";
    case "letter.sign": return "Signed engagement letter recorded";
    case "tb.import": return "Trial balance imported";
    case "gl.import": return "General ledger imported";
    case "pbc.send": return "Client requests sent";
    case "signoff.record": return "Workpaper signed off";
    default: return null;
  }
}

export interface ClosedRow { key: string; at: string; who: string; text: string; words: string | null }

/** Decisions from the log plus open items resolved or waived, newest first. `words` is what the person said when an agent recorded it. */
export function closedRows(log: Activity[], items: OpenItem[]): ClosedRow[] {
  const acts = log.filter((a) => !a.redacted).flatMap((a) => {
    const text = decisionText(a);
    return text ? [{ key: `a${a.seq}`, at: a.at, who: a.actor, text, words: a.instruction }] : [];
  });
  const done = items.filter((i) => i.status !== "open").map((i) => ({
    key: `o${i.id}`, at: i.resolvedAt ?? "", who: i.resolvedBy ?? "—",
    text: `${i.status === "waived" ? "Waived" : "Resolved"}: ${i.text}${i.resolution ? ` — ${i.resolution}` : ""}`, words: null,
  }));
  const sorted = [...acts, ...done].sort((a, b) => b.at.localeCompare(a.at));
  const out: Array<ClosedRow & { base: string; n: number }> = [];                        // the same act repeated by the same person in a row reads once, with a count
  for (const r of sorted) {
    const prev = out[out.length - 1];
    if (prev && prev.base === r.text && prev.who === r.who && !r.words && !prev.words) { prev.n += 1; prev.text = `${prev.base} (${prev.n} times)`; continue; }
    out.push({ ...r, base: r.text, n: 1 });
  }
  return out.map(({ base: _b, n: _n, ...r }) => r);
}

export const whenText = (at: string) => (at ? fmtStamp(at) : "");
